// adapted from https://github.com/dojo/framework/blob/master/src/core/util.ts

const hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * Type guard that ensures that the value can be coerced to Object
 * to weed out host objects that do not derive from Object.
 * This function is used to check if we want to deep copy an object or not.
 * Note: In ES6 it is possible to modify an object's Symbol.toStringTag property, which will
 * change the value returned by `toString`. This is a rare edge case that is difficult to handle,
 * so it is not handled here.
 * @param value The value to check
 * @returns If the value is coercible into an Object
 */
function shouldDeepCopyObject (value: any): value is Object {
	return Object.prototype.toString.call(value) === '[object Object]';
}

function copyArray<T> (array: T[], inherited: boolean): T[] {
	return array.map(function (item: T): T {
		if (Array.isArray(item)) {
			return copyArray(item, inherited) as any;
		}

		return !shouldDeepCopyObject(item)
			? item
			: _mixin({
					deep: true,
					inherited,
					sources: [item] as T[],
					target: {} as T,
			});
	});
}

interface MixinArgs<T extends {}, U extends {}> {
	deep: boolean;
	inherited: boolean;
	sources: Array<U | null | undefined>;
	target: T;
	copied?: any[];
}

/* tslint:disable-next-line cyclomatic-complexity */
function _mixin<T extends {}, U extends {}> (kwArgs: MixinArgs<T, U>): T & U {
	const deep = kwArgs.deep;
	const inherited = kwArgs.inherited;
	const target: any = kwArgs.target;
	const copied = kwArgs.copied || [];
	const copiedClone = [...copied];

	/* tslint:disable-next-line prefer-for-of */
	for (let i = 0; i < kwArgs.sources.length; i++) {
		const source = kwArgs.sources[i];

		if (source === null || source === undefined) {
			continue;
		}
		for (const key in source) {
			if (inherited || hasOwnProperty.call(source, key)) {
				let value: any = source[key];

				if (copiedClone.indexOf(value) !== -1) {
					continue;
				}

				if (deep) {
					if (Array.isArray(value)) {
						value = copyArray(value, inherited);
					} else if (shouldDeepCopyObject(value)) {
						const targetValue: any = target[key] || {};
						copied.push(source);
						value = _mixin({
							deep: true,
							inherited,
							sources: [value],
							target: targetValue,
							copied,
						});
					}
				}
				target[key] = value;
			}
		}
	}

	return target;
}

/**
 * Deep copy the values of all enumerable own properties of one or more source objects to the target object,
 * recursively copying all nested objects and arrays as well. `target` will be modified.
 *
 * @param target The target object to receive values from source objects
 * @param sources Any number of objects whose enumerable own properties will be copied to the target object
 * @returns The modified target object
 */
export function deepAssign<
	T extends {},
	U extends {},
	V extends {},
	W extends {},
	X extends {},
	Y extends {},
	Z extends {}
> (target: T, source1: U, source2: V, source3: W, source4: X, source5: Y, source6: Z): T & U & V & W & X & Y & Z;
export function deepAssign<T extends {}, U extends {}, V extends {}, W extends {}, X extends {}, Y extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W,
	source4: X,
	source5: Y
): T & U & V & W & X & Y;
export function deepAssign<T extends {}, U extends {}, V extends {}, W extends {}, X extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W,
	source4: X
): T & U & V & W & X;
export function deepAssign<T extends {}, U extends {}, V extends {}, W extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W
): T & U & V & W;
export function deepAssign<T extends {}, U extends {}, V extends {}> (target: T, source1: U, source2: V): T & U & V;
export function deepAssign<T extends {}, U extends {}> (target: T, source: U): T & U;
export function deepAssign (target: any, ...sources: any[]): any {
	return _mixin({
		deep: true,
		inherited: false,
		sources,
		target,
	});
}

/**
 * Shallow copy the values of all enumerable own properties of one or more source objects to the
 * target object. `target` will be modified.
 *
 * @returns The modified target object
 */
export function assign<T extends {}, U extends {}, V extends {}, W extends {}, X extends {}, Y extends {}, Z extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W,
	source4: X,
	source5: Y,
	source6: Z
): T & U & V & W & X & Y & Z;
export function assign<T extends {}, U extends {}, V extends {}, W extends {}, X extends {}, Y extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W,
	source4: X,
	source5: Y
): T & U & V & W & X & Y;
export function assign<T extends {}, U extends {}, V extends {}, W extends {}, X extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W,
	source4: X
): T & U & V & W & X;
export function assign<T extends {}, U extends {}, V extends {}, W extends {}> (
	target: T,
	source1: U,
	source2: V,
	source3: W
): T & U & V & W;
export function assign<T extends {}, U extends {}, V extends {}> (target: T, source1: U, source2: V): T & U & V;
export function assign<T extends {}, U extends {}> (target: T, source: U): T & U;
export function assign (target: any, ...sources: any[]): any {
	return _mixin({
		deep: false,
		inherited: false,
		sources,
		target,
	});
}
